/**
 * Timezone Utility Functions
 * Converts UTC dates to user's timezone for display
 */

// Timezone offset mappings (hours from UTC)
const TIMEZONE_OFFSETS = {
  'GMT': 0,
  'UTC': 0,
  'IST': 5.5,      // India Standard Time
  'EST': -5,       // Eastern Standard Time
  'EDT': -4,       // Eastern Daylight Time
  'PST': -8,       // Pacific Standard Time
  'PDT': -7,       // Pacific Daylight Time
  'CST': -6,       // Central Standard Time
  'CDT': -5,       // Central Daylight Time
  'MST': -7,       // Mountain Standard Time
  'MDT': -6,       // Mountain Daylight Time
  'AEST': 10,      // Australian Eastern Standard Time
  'AEDT': 11,      // Australian Eastern Daylight Time
  'JST': 9,        // Japan Standard Time
  'SGT': 8,        // Singapore Time
  'CET': 1,        // Central European Time
  'CEST': 2,       // Central European Summer Time
  'BST': 1,        // British Summer Time
};

/**
 * Get offset hours for a timezone code
 */
export const getTimezoneOffset = (tzCode) => {
  return TIMEZONE_OFFSETS[tzCode?.toUpperCase()] ?? 0;
};

/**
 * Convert a UTC date to specified timezone
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string} timezone - Timezone code (e.g., 'IST', 'EST', 'GMT')
 * @returns {Date} - Date object adjusted to the timezone
 */
export const convertToTimezone = (utcDate, timezone = 'GMT') => {
  if (!utcDate) return null;
  
  const date = new Date(utcDate);
  if (isNaN(date.getTime())) return null;
  
  const offset = getTimezoneOffset(timezone);
  const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;
  return new Date(utcTime + offset * 3600000);
};

/**
 * Format a UTC date/time in user's timezone
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string} timezone - Timezone code
 * @param {object} options - Formatting options
 * @returns {string} - Formatted date/time string
 */
export const formatInTimezone = (utcDate, timezone = 'GMT', options = {}) => {
  if (!utcDate) return '-';
  
  const {
    showDate = true,
    showTime = true,
    showTimezone = false,
    dateFormat = 'short', // 'short', 'medium', 'long'
    timeFormat = '12h'    // '12h', '24h'
  } = options;

  const localDate = convertToTimezone(utcDate, timezone);
  if (!localDate) return '-';

  const parts = [];
  
  if (showDate) {
    const dateOpts = {
      short: { month: 'numeric', day: 'numeric', year: '2-digit' },
      medium: { month: 'short', day: 'numeric', year: 'numeric' },
      long: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
    };
    parts.push(localDate.toLocaleDateString('en-US', dateOpts[dateFormat] || dateOpts.short));
  }
  
  if (showTime) {
    const timeOpts = timeFormat === '24h' 
      ? { hour: '2-digit', minute: '2-digit', hour12: false }
      : { hour: 'numeric', minute: '2-digit', hour12: true };
    parts.push(localDate.toLocaleTimeString('en-US', timeOpts));
  }
  
  let result = parts.join(' ');
  
  if (showTimezone) {
    result += ` ${timezone}`;
  }
  
  return result;
};

/**
 * Format time only (useful for clock-in/out displays)
 * @param {string|Date} utcDate - UTC date
 * @param {string} timezone - Timezone code
 * @returns {string} - Formatted time (e.g., "9:30 AM")
 */
export const formatTimeInTimezone = (utcDate, timezone = 'GMT') => {
  return formatInTimezone(utcDate, timezone, {
    showDate: false,
    showTime: true,
    timeFormat: '12h'
  });
};

/**
 * Format date only
 * @param {string|Date} utcDate - UTC date
 * @param {string} timezone - Timezone code  
 * @param {string} format - 'short', 'medium', 'long'
 * @returns {string} - Formatted date
 */
export const formatDateInTimezone = (utcDate, timezone = 'GMT', format = 'medium') => {
  return formatInTimezone(utcDate, timezone, {
    showDate: true,
    showTime: false,
    dateFormat: format
  });
};

/**
 * Format date and time with timezone indicator
 * @param {string|Date} utcDate - UTC date
 * @param {string} timezone - Timezone code
 * @returns {string} - Formatted datetime with timezone (e.g., "Jan 15, 2024 9:30 AM IST")
 */
export const formatDateTimeWithTz = (utcDate, timezone = 'GMT') => {
  return formatInTimezone(utcDate, timezone, {
    showDate: true,
    showTime: true,
    showTimezone: true,
    dateFormat: 'medium',
    timeFormat: '12h'
  });
};

/**
 * Get the user's timezone from localStorage or default
 * @returns {string} - User's timezone code
 */
export const getUserTimezone = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user.working_timezone || user.timezone || 'GMT';
};

/**
 * Format a date using user's stored timezone preference
 * @param {string|Date} utcDate - UTC date
 * @param {object} options - Formatting options
 * @returns {string} - Formatted string in user's timezone
 */
export const formatForUser = (utcDate, options = {}) => {
  const timezone = getUserTimezone();
  return formatInTimezone(utcDate, timezone, options);
};

export default {
  convertToTimezone,
  formatInTimezone,
  formatTimeInTimezone,
  formatDateInTimezone,
  formatDateTimeWithTz,
  getUserTimezone,
  formatForUser,
  getTimezoneOffset,
  TIMEZONE_OFFSETS
};
